import React from 'react';

/**
 * Client-side password strength meter. Mirrors (best-effort) the backend
 * validator at backend/routes/users.py → validate_password_strength.
 *
 * Rules:
 *   - at least 10 characters
 *   - at least three of: lowercase, uppercase, digit, symbol
 *   - not in a small list of catastrophically common passwords
 */

const COMMON = new Set([
  'password', 'password1', 'password123', 'passw0rd', '12345678', '123456789',
  'qwerty', 'qwerty123', 'abc12345', 'welcome', 'welcome1', 'iloveyou',
  'admin', 'admin123', 'letmein', 'monkey', 'dragon', 'football', 'baseball',
  'master', 'sunshine', 'princess', 'trustno1', 'login', 'starwars',
]);

export function scorePassword(pw) {
  if (!pw) return { score: 0, label: '', reasons: ['Enter a password'] };

  const reasons = [];
  let score = 0;

  if (pw.length >= 10) score += 1; else reasons.push('Use at least 10 characters');
  if (pw.length >= 14) score += 1;

  const classes =
    (/[a-z]/.test(pw) ? 1 : 0) +
    (/[A-Z]/.test(pw) ? 1 : 0) +
    (/[0-9]/.test(pw) ? 1 : 0) +
    (/[^A-Za-z0-9]/.test(pw) ? 1 : 0);
  if (classes >= 3) score += 1;
  if (classes >= 4) score += 1;
  else if (classes < 3) reasons.push('Mix lowercase, uppercase, digits, and symbols');

  if (COMMON.has(pw.toLowerCase())) {
    reasons.push('This password is too common — pick something unique');
    score = Math.min(score, 1);
  }

  const labels = ['Very weak', 'Weak', 'Fair', 'Good', 'Strong'];
  return { score, label: labels[Math.max(0, Math.min(4, score))], reasons };
}

const BAR_COLOURS = ['bg-red-500', 'bg-orange-500', 'bg-amber-500', 'bg-lime-500', 'bg-emerald-500'];

const PasswordStrengthMeter = ({ password, className = '' }) => {
  const { score, label, reasons } = scorePassword(password);
  if (!password) return null;

  return (
    <div className={`mt-1 ${className}`}>
      <div className="flex gap-1 mb-1.5" aria-hidden="true">
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full transition-colors ${
              i <= score ? BAR_COLOURS[score] : 'bg-slate-200'
            }`}
          />
        ))}
      </div>
      <p
        className="text-xs text-slate-600 flex items-center justify-between"
        role="status"
        aria-live="polite"
      >
        <span>Strength: <strong className="text-slate-800">{label}</strong></span>
        {reasons.length === 0 && <span className="text-emerald-600">Looks good</span>}
      </p>
      {reasons.length > 0 && (
        <ul className="text-xs text-slate-500 mt-1 list-disc pl-5">
          {reasons.map((r, i) => <li key={i}>{r}</li>)}
        </ul>
      )}
    </div>
  );
};

export default PasswordStrengthMeter;
